import React, { useState, useEffect } from 'react';
import { getSyncHistory, syncModelsNow } from '../../services/modelService';
import { Spinner } from '../common/Spinner';

interface ModelSyncStatusProps {
  darkMode?: boolean;
  isAdmin?: boolean;
}

export const ModelSyncStatus: React.FC<ModelSyncStatusProps> = ({
  darkMode = false,
  isAdmin = false
}) => {
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    fetchSyncHistory();
  }, []);
  
  const fetchSyncHistory = async () => {
    setLoading(true);
    try {
      console.log("Fetching sync history...");
      const data = await getSyncHistory();
      console.log("Sync history response:", data);
      setSyncHistory(data.sync_history || []);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching sync history:", err);
      setError(err.message || 'Failed to fetch sync history');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSync = async () => {
    if (syncing) return;
    
    setSyncing(true);
    setError(null);
    
    try {
      console.log("Starting model sync...");
      const result = await syncModelsNow(isAdmin);
      console.log("Sync result:", result);
      
      // Wait a bit for sync to complete
      console.log("Waiting for sync to complete...");
      setTimeout(() => {
        console.log("Refreshing sync history...");
        fetchSyncHistory();
        setSyncing(false);
      }, 2000);
    } catch (err: any) {
      console.error("Error syncing models:", err);
      setError(err.message || 'Failed to sync models');
      setSyncing(false);
    }
  };
  
  if (loading) return <div className="flex justify-center p-4"><Spinner size="md" /></div>;
  
  const lastSync = syncHistory.length > 0 ? syncHistory[0] : null;
  
  return (
    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border'}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Model Sync Status</h3>
        
        <button
          onClick={handleSync}
          disabled={syncing}
          className={`px-4 py-2 rounded ${
            darkMode 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>
      
      {error && (
        <div className={`p-3 mb-4 rounded ${
          darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
        }`}>
          {error}
        </div>
      )}
      
      <div className={`p-3 mb-4 rounded ${
        darkMode ? 'bg-gray-700' : 'bg-gray-100'
      }`}>
        <div className="grid grid-cols-2 gap-2">
          <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Last Sync:</div>
          <div>{lastSync ? new Date(lastSync.timestamp).toLocaleString() : 'Never'}</div>
          
          <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Status:</div>
          <div className={lastSync?.success 
            ? 'text-green-500' 
            : 'text-red-500'
          }>
            {lastSync?.success ? 'Success' : 'Failed'}
          </div>
          
          {lastSync?.success && (
            <>
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Models Updated:</div>
              <div>{lastSync.created + lastSync.updated}</div>
              
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Duration:</div>
              <div>{lastSync.duration_seconds?.toFixed(2) || '?'} seconds</div>
            </>
          )}
          
          {!lastSync?.success && lastSync?.error && (
            <>
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Error:</div>
              <div className="text-red-500">{lastSync.error}</div>
            </>
          )}
        </div>
      </div>
      
      <h4 className="font-medium mb-2">Recent Syncs</h4>
      <div className="overflow-x-auto">
        <table className={`min-w-full ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          <thead>
            <tr className={darkMode ? 'border-gray-700' : 'border-gray-200'}>
              <th className="text-left py-2">Time</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right py-2">Created</th>
              <th className="text-right py-2">Updated</th>
              <th className="text-right py-2">Duration</th>
            </tr>
          </thead>
          <tbody>
            {syncHistory.map((sync, index) => (
              <tr key={index} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className="py-2">{new Date(sync.timestamp).toLocaleString()}</td>
                <td className={`py-2 ${sync.success ? 'text-green-500' : 'text-red-500'}`}>
                  {sync.success ? 'Success' : 'Failed'}
                </td>
                <td className="text-right py-2">{sync.created || '-'}</td>
                <td className="text-right py-2">{sync.updated || '-'}</td>
                <td className="text-right py-2">{sync.duration_seconds?.toFixed(2) || '-'} s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
